import React, { useEffect, useRef, useState } from 'react'
import { Checkbox, Col, Icon, Modal, notification, Row } from 'antd'
import { Formik } from 'formik'
import { useSelector } from 'react-redux'

import { ApplicationState } from 'src/core/store'
import { Button, Table, Text, ToolIcon } from 'src/components/Ant'
import { ColumnProps } from 'src/components/Ant/Table'
import {
  Id,
  Policy,
  PolicyConferenceRules,
  PolicyFlowConference,
  PolicyFlowFormat,
  PolicyFlowType,
  PolicyTree
} from 'src/types'
import { Select } from 'src/components/Formik'
import { useT } from 'src/core/hooks/use-translation'
import * as PoliciesService from 'src/services/api/policies-flow'
import * as PoliciesTreeService from 'src/services/api/policies-trees'

import { validationSchema } from './validation-schema'
import * as Values from './values'
import sanitize from './sanitize'

import { PropsTreeSelected } from '../..'
import FlowConferencesModal from '../../../flow/conferences/flow-conference-modal'
import HierarchyFlowsConferencesExceptionModal from '../hierarchy-flows-conferences-exception-modal'
import HierarchyFlowsBindExceptionStructure from '../hierarchy-flows-structure-exception-rule'

type Props = {
  policies: Policy[]
  policyTree: PolicyTree[]
  resetPoliciesTrees: () => void
  treesSelected: PropsTreeSelected
}
type PolicyFlowConferenceList = PolicyFlowConference & { key: Id }

export default function HierarchyFlowsConferences({
  policies,
  policyTree,
  resetPoliciesTrees,
  treesSelected
}: Props) {
  const { me } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  const refFormik = useRef()
  const refFormikConferenceModal = useRef<Formik<PolicyFlowConference>>(null)
  const refFormikExceptionModal = useRef<Formik<PolicyConferenceRules>>(null)

  const { t } = useT()

  const [loading, setLoading] = useState(false)
  const [dataSource, setDataSource] = useState<PolicyFlowConferenceList[]>([])
  const [selectedRule, setSelectedRule] = useState<PolicyConferenceRules>()
  const [listConferencesRules, setListConferencesRules] = useState<
    PolicyConferenceRules[]
  >([])
  const [modalPolicyFlowFormat, setModalPolicyFlowFormat] =
    useState<PolicyFlowFormat>(PolicyFlowFormat.STANDARD)
  const [modalPolicyFlowType, setModalPolicyFlowType] =
    useState<PolicyFlowType>(PolicyFlowType.HIERARCHY)

  function setModalPolicyFlow(record: PolicyFlowConference): void {
    let type = PolicyFlowType.HIERARCHY
    let format = PolicyFlowFormat.STANDARD

    if (record.automatic_conference) {
      format = PolicyFlowFormat.AUTOMATIC
    }
    if (record.consensual_conference) {
      format = PolicyFlowFormat.CONSENSUS
    }

    if (record.cost_center_conference) {
      type = PolicyFlowType.COST_CENTER
    }
    if (record.reason_conference) {
      type = PolicyFlowType.REASON
    }
    setModalPolicyFlowFormat(format)
    setModalPolicyFlowType(type)
  }

  function UpdatePolicyTree() {
    PoliciesService.getRulesException({
      organization_id: me.organization.organization_id,
      policy_name: 'conference',
      policy_id: policyTree[0].policy_conference_id
    })
      .then(response => {
        if (response.status === 200) {
          setListConferencesRules(response.data as PolicyConferenceRules[])
        } else {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    setLoading(true)
    PoliciesService.getPolicyConferences()
      .then(response => {
        if (response.status === 200) {
          const conferences: any[] =
            response.data?.policy_conferences.map(data => ({
              ...data,
              //@ts-ignore
              key: data.policy_conference_id
            })) || []
          setDataSource(conferences)
        } else {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(() => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
        //Send Bugsnag?
      })
      .finally(() => {
        setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (selectedRule) {
      setSelectedRule(
        listConferencesRules.find(
          rule =>
            rule.policy_conference_rule_id ===
            selectedRule.policy_conference_rule_id
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listConferencesRules])

  useEffect(() => {
    if (policyTree?.length && policyTree[0].policy_conference_id) {
      setLoading(true)
      PoliciesService.getRulesException({
        organization_id: me.organization.organization_id,
        policy_name: 'conference',
        policy_id: policyTree[0].policy_conference_id
      })
        .then(response => {
          if (response.status === 200) {
            setListConferencesRules(response.data as PolicyConferenceRules[])
          } else {
            notification.error({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          }
        })
        .catch(response => {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        })
        .finally(() => {
          setLoading(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyTree])

  const optionsConferences = dataSource.map(conference => ({
    value: String(conference.policy_conference_id),
    label: conference.name
  }))

  function LinkPolicyTree(value: any) {
    Modal.confirm({
      cancelText: t('common.actions.cancel'),
      content: t('web.tree.policies.modal.message'),
      onOk: () =>
        PoliciesTreeService.save({
          assign_tree_id: treesSelected.id,
          organization_id: me.organization.organization_id,
          policy: 'policy_conference_id',
          policy_id: value.policy
        })
          .then(response => {
            if (response.status === 200) {
              resetPoliciesTrees()
            } else {
              notification.error({
                message: t('common.response.status-500'),
                placement: 'bottomRight'
              })
            }
          })
          .catch(() => {
            notification.error({
              message: t('common.response.status-500'),
              placement: 'bottomRight'
            })
          })
    })
  }

  function UpdateActive(row: PolicyConferenceRules) {
    PoliciesService.SaveRulesException({
      organization_id: me.organization.organization_id,
      policy_conference_rules: [{ ...row, active: !row.active }],
      policy_id: row.policy_conference_id,
      policy_name: 'conference'
    })
      .then(response => {
        if (response.status === 200) {
          resetPoliciesTrees()
        } else {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(() => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
  }

  const columns: ColumnProps<PolicyTree>[] = [
    {
      title: 'web.tree.flows.table.code',
      dataIndex: 'policy_conference.code',
      key: 'code'
    },
    {
      title: 'web.tree.flows.table.name',
      dataIndex: 'policy_conference.name',
      key: 'name'
    },
    {
      align: 'center',
      key: 'view',
      dataIndex: 'policy_conference',
      render: (record: PolicyFlowConference) => (
        <Icon
          type="eye"
          onClick={() => {
            setModalPolicyFlow(record)
            refFormikConferenceModal.current.resetForm(record)
          }}
        />
      ),
      title: 'web.tree.flows.table.view'
    }
  ]

  const columnsRules: ColumnProps<PolicyConferenceRules>[] = [
    {
      title: 'web.tree.flows.table-rule.code',
      dataIndex: 'code',
      key: 'code'
    },
    {
      title: 'web.tree.flows.table-rule.name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'web.tree.flows.table-rule.link',
      key: 'link',
      align: 'center',
      render: (row: PolicyConferenceRules) => (
        <Icon
          type="link"
          onClick={() => {
            setSelectedRule(row)
          }}
        />
      )
    },
    {
      align: 'center',
      key: 'active',
      render: (row: PolicyConferenceRules) => (
        <Checkbox checked={row.active} onChange={() => UpdateActive(row)} />
      ),
      title: 'web.tree.flows.table-rule.active'
    },
    {
      align: 'center',
      key: 'edit',
      render: (row: PolicyConferenceRules) => (
        <Icon
          type="edit"
          onClick={() => {
            refFormikExceptionModal.current.resetForm(row)
          }}
        />
      ),
      title: 'web.tree.flows.table-rule.edit'
    }
  ]

  function updateSelectedRule(update?: boolean): void {
    if (!update) {
      setSelectedRule(undefined)
      return
    }

    UpdatePolicyTree()
  }

  function handleConferencesExceptionSubmit(
    values: PolicyConferenceRules
  ): void {
    const policy_conference_rules = [sanitize(values)]

    setLoading(true)
    PoliciesService.SaveRulesException({
      organization_id: me.organization.organization_id,
      policy_id: policyTree?.[0]?.policy_conference.policy_conference_id,
      policy_conference_rules,
      policy_name: 'conference'
    })
      .then(response => {
        if (response.status === 200) {
          const record = response.data[0] as PolicyConferenceRules
          if (typeof values.policy_conference_rule_id === 'string') {
            setListConferencesRules(list => ({ ...list, record }))
          } else {
            setListConferencesRules(list =>
              list.map(item =>
                item.policy_conference_rule_id !==
                record.policy_conference_rule_id
                  ? item
                  : record
              )
            )
          }
          refFormikExceptionModal.current.resetForm(Values.initialValues)
        } else {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Formik
        initialValues={{ policy: '' }}
        onSubmit={LinkPolicyTree}
        ref={refFormik}
        render={formikProps => (
          <Row>
            <Col span={15}>
              <Select
                dataTestId="organizations-list"
                formikProps={formikProps}
                label={'web.tree.flows.conferences.flow-title'}
                name="policy"
                options={optionsConferences}
                placeholder="common.actions.select"
                required={false}
              />
            </Col>
            <Col span={9}>
              <Button
                disabled={formikProps.values.policy === ''}
                type="default"
                onClick={() => {
                  const foundPolicy = dataSource.find(
                    policy =>
                      `${policy.policy_conference_id}` ===
                      `${formikProps.values.policy}`
                  )
                  if (foundPolicy) {
                    setModalPolicyFlow(foundPolicy)
                    refFormikConferenceModal.current.resetForm(foundPolicy)
                  }
                }}
                style={{ margin: '40px 10px 0px 10px' }}
              >
                <ToolIcon key="eye" title="web.receipts.card.view" type="eye" />
              </Button>

              <Button
                disabled={formikProps.values.policy === ''}
                onClick={() => {
                  formikProps.submitForm()
                }}
              >
                {t('web.tree.policies.link')}
              </Button>
            </Col>
          </Row>
        )}
      />

      <div>
        <Text style={{ fontSize: '25px', color: '#000' }} strong>
          {t('web.tree.flows.flow-link')}
        </Text>
        <Table
          dataSource={policyTree?.[0]?.policy_conference_id ? policyTree : []}
          loading={loading}
          className="table-striped"
          columns={columns}
          size="small"
        />
      </div>
      {policyTree?.[0]?.policy_conference.reason_conference && (
        <div>
          <Text style={{ fontSize: '25px', color: '#000' }} strong>
            {t('web.tree.flows.conferences.rules.title')}
          </Text>
          <Table
            dataSource={listConferencesRules}
            loading={loading}
            className="table-striped"
            columns={columnsRules}
            size="small"
          />
          <div style={{ display: 'flex', justifyContent: 'end' }}>
            <Button
              onClick={() => {
                refFormikExceptionModal.current.resetForm(
                  Values.getDefaultValues({
                    policy_conference_id:
                      policyTree?.[0]?.policy_conference.policy_conference_id,
                    organization_id: me.user.organization_id
                  })
                )
              }}
              type="primary"
            >
              <ToolIcon type="plus" style={{ fontWeight: 'bold' }} />
              {t('web.tree.flows.conferences.rules.title')}
            </Button>
          </div>
        </div>
      )}
      <Formik
        initialValues={{} as any}
        onSubmit={undefined}
        ref={refFormikConferenceModal}
        render={formikProps => (
          <FlowConferencesModal
            disabled
            formikProps={formikProps}
            isVisible={Boolean(formikProps?.values?.policy_conference_id)}
            modalPolicyFlowFormat={modalPolicyFlowFormat}
            modalPolicyFlowType={modalPolicyFlowType}
            refFormik={refFormikConferenceModal}
          />
        )}
      />
      <Formik
        initialValues={Values.initialValues}
        onSubmit={handleConferencesExceptionSubmit}
        ref={refFormikExceptionModal}
        render={formikProps => (
          <HierarchyFlowsConferencesExceptionModal
            formikProps={formikProps}
            policies={policies}
          />
        )}
        validationSchema={validationSchema}
      />
      <HierarchyFlowsBindExceptionStructure
        selectedRule={selectedRule}
        toggle={updateSelectedRule}
        treesSelected={treesSelected}
      />
    </>
  )
}
